import { LandingPages } from '@/modules/landing/store/useLandingStore';
import { RouteRecordRaw, createRouter, createWebHistory } from 'vue-router';

import { isDashboardNewRoute } from '@/helpers/router';
import { routes as onboardingChildren } from '../modules/onboarding/router';

import MainLayout from '@/components/layout/Main.vue';
import EmptyLayout from '@/components/layout/Empty.vue';

import LandingModule from '@/modules/landing/LandingModule.vue';
import ViewerModule from '@/modules/viewer/ViewerModule.vue';
import ShowViewer from '@/modules/viewer/components/Show.vue';
import NotFoundModule from '@/modules/error/NotFoundModule.vue';

// Lazy: Annotations reaches the whole media editor (PixiJS, mediabunny, the
// local exporter — several hundred modules). A static import puts all of it in
// the main chunk, and in dev into every page's initial module graph.
const Annotations = () => import('@/modules/viewer/components/Annotations.vue');

import Sidebar from '@/components/sidebar/SidebarWrapper.vue';
import Header from '@/components/layout/header/Header.vue';
import IntegrationsModule from '@/modules/dashboard/integrations/IntegrationsModule.vue';
import RequestContentsModule from '@/modules/dashboard/requestContents/RequestContentsModule.vue';
import RequestContentsUpselling from '@/modules/dashboard/requestContents/RequestContentsUpselling.vue';
import AiAgentsModule from '@/modules/dashboard/aiAgents/AiAgentsModule.vue';
import { useFeatures } from '@/composables/useFeatures';
import { useEntitlementsStore } from '@/store/useEntitlementsStore';
import { useOrganizationStore } from '@/store/useOrganizationStore';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/store/useUserStore';

export enum SIDEBAR_TYPES {
  LOGGED_IN = 'dashboard-sidebar',
  GUEST = 'landing-sidebar',
}

declare module 'vue-router' {
  interface RouteMeta {
    title: string;
    icon: string;
    badge?: string;
    visible?: () => boolean;
    newTab?: boolean;
    href?: string | (() => string);
  }
}

export default (domain: string) => {
  const routes: RouteRecordRaw[] = [
    {
      path: '',
      component: MainLayout,
      children: [
        {
          path: '/dashboard',
          name: 'dashboard',
          components: {
            sidebar: Sidebar,
            default: EmptyLayout,
            header: Header,
          },
          redirect: () => ({ name: 'main' }),
          children: [
            {
              path: '',
              name: 'main',
              component: ViewerModule,
            },
          ],
        },
        // For now its only redirects - next steps - move this routers to main routing level
        {
          path: '/dashboard-new',
          name: SIDEBAR_TYPES.LOGGED_IN,
          components: {
            sidebar: Sidebar,
            default: EmptyLayout,
            header: Header,
          },
          redirect: { name: 'dashboard-new-404' },
          children: [
            {
              path: 'home',
              name: 'home',
              component: () => {},
              meta: {
                href: `${domain}/home`,
                title: 'Home',
                icon: 'general.home',
              },
            },
            {
              path: 'all-files',
              name: 'all-files',
              component: () => {},
              meta: {
                href: `${domain}/dashboard`,
                title: 'All Files',
                icon: 'general.file',
              },
            },
            {
              path: 'collections',
              name: 'collections',
              component: () => {},
              meta: {
                href: `${domain}/collections`,
                title: 'Collections',
                icon: 'general.folder',
              },
            },
            {
              path: 'agents',
              name: 'agents',
              // Two UXs share this route: the new Foundry agents module when
              // the `foundry-agents` flag is on, and the older AiAgentsModule
              // marketing/overview when it's off. A single sidebar entry
              // keeps nav simple; the flag toggles which experience loads.
              component: async () => {
                const { getFeature } = useFeatures();
                if (getFeature('foundry-agents', false).value) {
                  return (await import('@/modules/agents')).AgentsModule;
                }
                return AiAgentsModule;
              },
              meta: {
                title: 'Agents',
                icon: 'ai.robot',
                badge: 'new',
              },
            },
            {
              path: 'requests',
              name: 'request-contents',
              component: async () => {
                const entitlementsStore = useEntitlementsStore();
                if (!entitlementsStore.can('requestContent')) {
                  return RequestContentsUpselling;
                }
                return RequestContentsModule;
              },
              meta: {
                title: 'Request links',
                icon: 'general.requestLinks',
                visible: () => useEntitlementsStore().can('requestContent'),
              },
            },
            {
              path: 'meetings',
              name: 'meetings',
              component: () => {},
              meta: {
                href: `${domain}/dashboard?type=meeting`,
                title: 'Meetings',
                icon: 'general.calendar',
                badge: 'beta',
              },
            },
            {
              path: 'favorites',
              name: 'favorites',
              component: () => {},
              meta: {
                href: `${domain}/dashboard?type=favorite`,
                title: 'Favorites',
                icon: 'general.favorites',
              },
            },
            {
              path: 'integrations', // New route for integrations
              name: 'workflows-integrations',
              component: IntegrationsModule,
              meta: {
                title: 'Integrations',
                icon: 'general.interactions',
              },
            },
            {
              path: 'zightai',
              name: 'zightai',
              component: () => {},
              meta: {
                href: () => {
                  const { isLoggedIn } = storeToRefs(useUserStore());
                  const { getFeature } = useFeatures();
                  const { isOrgUserAdmin } = storeToRefs(useUserStore());

                  if (!isLoggedIn.value) {
                    return `https://www.zight.com/ai/`;
                  }

                  if (getFeature('addon_ai_enabled', false).value) {
                    const { id } = storeToRefs(useOrganizationStore());
                    return `${domain}/organizations/${id?.value}/account/settings#zightai`;
                  }

                  if (isOrgUserAdmin.value) {
                    return `${domain}/plans`;
                  }

                  return `${domain}/ai`;
                },
                title: 'Zight AI',
                icon: 'general.stars',
              },
            },
            {
              path: 'history',
              name: 'history',
              component: () => {},
              meta: {
                href: `${domain}/dashboard/activity`,
                title: 'History',
                icon: 'general.clock',
                visible: isDashboardNewRoute,
              },
            },
            {
              path: 'trash',
              name: 'trash',
              component: () => {},
              meta: {
                href: `${domain}/dashboard/trash`,
                title: 'Trash',
                icon: 'general.trash',
                visible: isDashboardNewRoute,
              },
            },
          ],
        },
        {
          path: '/dashboard-new/:pathMatch(.*)*',
          name: 'dashboard-new-404',
          component: NotFoundModule,
        },
        {
          path: '/:itemId/landing',
          name: SIDEBAR_TYPES.GUEST,
          components: {
            sidebar: Sidebar,
            default: EmptyLayout,
            header: Header,
          },
          redirect: () => ({ name: 'all-files-landing' }),
          children: [
            {
              path: 'all-files',
              name: 'all-files-landing',
              component: LandingModule,
              props: {
                type: LandingPages.ALL_FILES,
              },
              meta: {
                title: 'All Files',
                icon: 'general.home',
              },
            },
            {
              path: 'collections',
              name: 'collections-landing',
              component: LandingModule,
              props: {
                type: LandingPages.COLLECTIONS,
              },
              meta: {
                title: 'Collections',
                icon: 'general.folder',
              },
            },
            {
              path: 'request-links',
              name: 'request-links-demo',
              component: LandingModule,
              props: {
                type: LandingPages.REQUEST_VIDEO,
              },
              meta: {
                title: 'Request links',
                icon: 'general.requestLinks',
              },
            },
            {
              path: 'favorites',
              name: 'favorites-landing',
              component: LandingModule,
              props: {
                type: LandingPages.FAVORITES,
              },
              meta: {
                title: 'Favorites',
                icon: 'general.favorites',
              },
            },

            {
              path: 'integrations-landing',
              name: 'integrations-landing',
              component: () => {},
              meta: {
                href: () => {
                  const { isLoggedIn } = storeToRefs(useUserStore());

                  if (!isLoggedIn.value) {
                    return `https://www.zight.com/integrations/`;
                  }

                  return `${domain}/integrations`;
                },
                title: 'Integrations',
                icon: 'general.interactions',
              },
            },
            {
              path: 'agents',
              name: 'ai-agents-landing',
              component: AiAgentsModule,
              meta: {
                title: 'Agents',
                icon: 'ai.robot',
              },
            },
            {
              path: 'zightai',
              name: 'zightai',
              component: () => {},
              meta: {
                href: `https://www.zight.com/ai/`,
                title: 'Zight AI',
                icon: 'general.stars',
              },
            },
            {
              path: 'history',
              name: 'history-landing',
              component: LandingModule,
              props: {
                type: LandingPages.HISTORY,
              },
              meta: {
                title: 'History',
                icon: 'general.clock',
              },
            },
          ],
        },
        {
          path: '/:itemId',
          components: {
            default: ViewerModule,
            sidebar: Sidebar,
            header: Header,
          },
          name: 'viewer',
          children: [
            {
              path: '',
              name: 'viewerMain',
              component: ShowViewer,
            },
            {
              path: 'analytics',
              name: 'viewerAnalytics',
              redirect: () => ({ name: 'viewerMain', hash: '#analytics' }),
            },
          ],
        },
        {
          path: '/:itemId/edit',
          name: 'annotations',
          component: Annotations,
        },
        {
          path: '/:itemId/annotate',
          name: 'annotationsImageRedirect',
          redirect: () => ({ name: 'annotations' }),
        },
        {
          path: '/:itemId/annotate-video',
          name: 'annotationsVideoRedirect',
          redirect: () => ({ name: 'annotations' }),
        },
        {
          path: '/logout',
          name: 'logout',
          component: () => {},
        },
        {
          path: '/onboarding',
          name: 'onboarding',
          component: () => import('../modules/onboarding/OnboardingModule.vue'),
          children: onboardingChildren,
        },
        {
          path: '/:pathMatch(.*)*',
          name: 'not-found',
          component: NotFoundModule,
        },
      ],
    },
  ];

  return createRouter({
    history: createWebHistory(),
    routes,
  });
};
